import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import findResult from '@/utils/resultsHelper';
import { readStates, states } from '@/constants/lifecycle';
import { workspaceForms } from '../constants';

const useWorkspaceRequestsState = (code) => {
  const { requests } = useSelector(({ requests: r }) => r);
  return findResult(requests, 'workspaces', code);
};

const useWorkspaceDialogs = () => {
  const { role: { permissions: { requests: { create } } } } = useSelector(({ auth }) => auth);
  const { activeRequestReceipt } = useSelector(({ workspaces }) => workspaces);
  return { allowReceipt: activeRequestReceipt.open && create };
};

const makeSelectWorkspacePanelContainer = (code) => createSelector(
  ({ requests: r }) => r.requests,
  ({ workspaces }) => workspaces.forms,
  (requests, forms) => {
    const safeWorkspace = findResult(requests, 'workspaces', code)
      || { isDirty: false, ...readStates, isDefault: true };
    const { readStatus } = forms.health[workspaceForms.REQUEST];
    return {
      canCreate: readStatus === states.SUCCESS,
      exists: !safeWorkspace.isDefault,
      form: findResult(forms, 'open', workspaceForms.REQUEST),
      healthCheckReadStatus: readStatus,
      isDirty: safeWorkspace.isDirty,
      readErrors: safeWorkspace.readErrors,
      readStatus: safeWorkspace.readStatus,
    };
  },
);

const useWorkspacePanelContainer = ({ code }) => {
  const selector = useMemo(() => makeSelectWorkspacePanelContainer(code), [code]);
  return useSelector(selector);
};

const useWorkspaceSteps = ({ code }) => {
  const { steps: { activeStepIndex }, saveStatus } = useWorkspaceRequestsState(code);
  return { activeStepIndex, saveStatus };
};

const useWorkspaceRequestWizardActions = ({ code }) => {
  const workspace = useWorkspaceRequestsState(code);
  const { steps: { activeStepIndex }, saveStatus, markdown } = workspace || {
    steps: { activeStepIndex: 0 },
    saveStatus: states.NONE,
    markdown: '',
  };
  return { activeStepIndex, markdown, saveStatus };
};

const useWorkspaceRequestWizard = ({ code }) => {
  const { steps: { activeStepIndex } } = useWorkspaceRequestsState(code);
  return { activeStepIndex };
};

const useWorkspaceRequestConfirmation = ({ code }) => {
  const { saveStatus, saveErrors, markdown } = useWorkspaceRequestsState(code);
  return { saveStatus, saveErrors, markdown };
};

const makeSelectWorkspaceRequestInfo = (code) => createSelector(
  ({ requests: r }) => r.requests,
  ({ workspaces }) => workspaces.forms,
  (requests, forms) => {
    const workspace = findResult(requests, 'workspaces', code);
    const { sections, readErrors, readStatus } = findResult(forms, 'open', workspaceForms.REQUEST);
    return {
      fields: sections.workspace.fields,
      readErrors,
      readStatus,
      saveErrors: workspace.saveErrors,
      saveStatus: workspace.saveStatus,
      workspace,
    };
  },
);

const useWorkspaceRequestInfo = ({ code }) => {
  const selector = useMemo(() => makeSelectWorkspaceRequestInfo(code), [code]);
  return useSelector(selector);
};

// Tier 1 — single slice, no function calls
const useWorkspaceRequestDialog = () => {
  const { activeRequestReceipt: { name, text } } = useSelector(({ workspaces }) => workspaces);
  return { name, text };
};

// Tier 4 — two slices + non-trivial derivation
const makeSelectWorkspaceRequestActions = (code) => createSelector(
  ({ requests: r }) => r.requests,
  ({ workspaces }) => workspaces.forms,
  (requests, forms) => {
    const workspace = findResult(requests, 'workspaces', code);
    return {
      form: findResult(forms, 'open', workspaceForms.REQUEST),
      saveStatus: workspace.saveStatus,
      workspace,
    };
  },
);

const useWorkspaceRequestActions = ({ code }) => {
  const selector = useMemo(() => makeSelectWorkspaceRequestActions(code), [code]);
  return useSelector(selector);
};

// Tier 2 — single slice + findResult
const useWorkspaceRequestCompleteActions = ({ code }) => {
  const { saveStatus } = useWorkspaceRequestsState(code);
  return { saveStatus };
};

export default {
  useWorkspaceDialogs,
  useWorkspacePanelContainer,
  useWorkspaceRequestActions,
  useWorkspaceRequestCompleteActions,
  useWorkspaceRequestConfirmation,
  useWorkspaceRequestDialog,
  useWorkspaceRequestInfo,
  useWorkspaceRequestWizard,
  useWorkspaceRequestWizardActions,
  useWorkspaceSteps,
};
